import { Btn } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { reviewLabel } from "./format";
import {
  REVIEW_STATUSES,
  SEGMENTS,
  activeFilterCount,
  EMPTY_FILTERS,
  toggleReviewStatus,
  type CandidateMode,
  type FacilityFilters,
} from "./query";
import type { FacilitySegment } from "./types";

interface Props {
  filters: FacilityFilters;
  onChange: (filters: FacilityFilters) => void;
}

const CANDIDATE_LABEL: Record<CandidateMode, string> = {
  exclude: "Confirmed in scope",
  include: "Include possible matches",
  only: "Possible matches only",
};

export function FacilityFiltersBar({ filters, onChange }: Props) {
  const set = (patch: Partial<FacilityFilters>) => onChange({ ...filters, ...patch });
  const active = activeFilterCount(filters);

  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray w-[220px]">
        Name, city or postal code
        <Input value={filters.q} onChange={(e) => set({ q: e.target.value })} placeholder="Dow, Freeport, 77541" />
      </label>
      <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray w-[80px]">
        State
        <Input
          value={filters.state}
          maxLength={2}
          onChange={(e) => set({ state: e.target.value.toUpperCase() })}
          placeholder="TX"
        />
      </label>
      <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray">
        Segment
        <select
          className="ctl"
          value={filters.segment}
          onChange={(e) => set({ segment: e.target.value as FacilitySegment | "" })}
        >
          <option value="">All segments</option>
          {SEGMENTS.map((segment) => (
            <option key={segment} value={segment}>
              {segment}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="flex flex-col gap-1.5 text-xs text-rs-light-gray m-0 p-0 border-none min-w-0">
        <legend className="p-0 mb-1.5">Classification</legend>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] pt-2">
          {REVIEW_STATUSES.map((status) => (
            <label key={status} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.reviewStatuses.includes(status)}
                onChange={() => set({ reviewStatuses: toggleReviewStatus(filters.reviewStatuses, status) })}
              />
              {status === "Verified" ? "Verified" : reviewLabel(status)}
            </label>
          ))}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filters.reviewStatuses.length === REVIEW_STATUSES.length}
              onChange={() => set({ reviewStatuses: toggleReviewStatus(filters.reviewStatuses, "All") })}
            />
            All
          </label>
        </div>
      </fieldset>
      <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray w-[120px]">
        NAICS code
        <Input
          value={filters.naics}
          maxLength={6}
          inputMode="numeric"
          onChange={(e) => set({ naics: e.target.value.replace(/\D/g, "") })}
          placeholder="325211"
        />
      </label>
      <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray">
        Show
        <select
          className="ctl"
          value={filters.candidates}
          onChange={(e) => set({ candidates: e.target.value as CandidateMode })}
        >
          {(Object.keys(CANDIDATE_LABEL) as CandidateMode[]).map((mode) => (
            <option key={mode} value={mode}>
              {CANDIDATE_LABEL[mode]}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-[13px] text-rs-light-gray pb-2.5">
        <input
          type="checkbox"
          checked={filters.unmatched}
          onChange={(e) => set({ unmatched: e.target.checked })}
        />
        Not yet attached to a supplier
      </label>
      {(active > 0 || filters.q || filters.state || filters.naics) && (
        <Btn variant="ghost" onClick={() => onChange(EMPTY_FILTERS)}>
          Clear Filters
        </Btn>
      )}
    </div>
  );
}
